import { Accept, useDropzone } from 'react-dropzone'
import { useIntl } from 'react-intl'

import Box from 'app/components/base/Box/Box.web'
import FormControl from 'app/components/base/FormControl'
import useFormField from 'app/hooks/useFormField'
import { combineControllerProps } from 'app/utils/helpers/form.helpers'

import { UploadFieldCommonProps } from './UploadField.types'
import { uploadFieldValidator } from './UploadField.utils'
import useUploadField from './useUploadField'

export type UploadFieldProps = UploadFieldCommonProps & {
  accept?: Accept
  isImageUpload?: boolean
}

const UploadField: RFC<UploadFieldProps> = ({
  maxSize = 1024 * 1024 * 25,
  error: customError = null,
  placeholder,
  label,
  helperText,
  required,
  FormControlProps,
  accept,
  multiple = false,
  isDnd = false,
  disabled,
  renderComponent,
  renderInput,
  uploadFile,
  onUploadSuccess,
  onUploadFail,
  description,
  ...props
}) => {
  const { controllerProps } = combineControllerProps(props)
  const { value, onChange, error = customError } = useFormField(controllerProps)
  const intl = useIntl()

  const { files, deleteFile, uploadFiles, changePosition, isProcessing } =
    useUploadField({
      maxSize,
      multiple,
      uploadFile,
      onChange,
      value,
      onSuccess: onUploadSuccess,
      onError: onUploadFail
    })

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple,
    maxSize,
    disabled: disabled || isProcessing,
    validator: ({ size }) => {
      const error = uploadFieldValidator({
        size,
        maxSize,
        formatMessage: intl.formatMessage
      })

      return error
    },
    onDrop: async (acceptedFiles) => {
      uploadFiles(acceptedFiles.map((file) => ({ file })))
    }
  })

  const renderProps = {
    value: files,
    error,
    isDnd,
    multiple,
    onDelete: deleteFile,
    onUpload: () => {},
    onChangePosition: changePosition,
    getRootProps,
    getInputProps,
    disabled,
    description
  }

  return (
    <FormControl
      error={!!error}
      errorMessage={error}
      label={label}
      helperText={helperText}
      required={required}
      {...FormControlProps}
    >
      <Box component="section">
        {renderInput(renderProps)}

        {renderComponent && renderComponent(renderProps)}
      </Box>
    </FormControl>
  )
}

export default UploadField
